import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { formatDistanceToNow, format } from "date-fns";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send, MessageSquare, History } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Textarea } from "@/components/ui/Textarea";
import { Separator } from "@/components/ui/Separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { useAuth } from "@/contexts/AuthContext";
import {
  ticketsApi,
  ApiError,
  type TicketDetail,
  type TicketStatus,
  type TicketPriority,
} from "@/lib/api";

export function TenantTicketDetail({ tenantCode }: { tenantCode: string }) {
  const { id = "" } = useParams<{ id: string }>();
  const { idToken } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = useQuery<TicketDetail, ApiError>({
    queryKey: ["ticket", tenantCode, id],
    queryFn: () => ticketsApi.get(tenantCode, id, idToken!),
    enabled: !!idToken && !!id,
  });

  const updateMutation = useMutation({
    mutationFn: async (patch: { status?: TicketStatus; priority?: TicketPriority }) => {
      return ticketsApi.update(tenantCode, id, patch, idToken!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket", tenantCode, id] });
      qc.invalidateQueries({ queryKey: ["tickets", tenantCode] });
      toast.success("Ticket updated");
    },
    onError: (err: ApiError) => {
      toast.error("Couldn't update", { description: err.message });
    },
  });

  const [note, setNote] = useState("");
  const addNoteMutation = useMutation({
    mutationFn: async () => ticketsApi.addNote(tenantCode, id, note, idToken!),
    onSuccess: () => {
      setNote("");
      qc.invalidateQueries({ queryKey: ["ticket", tenantCode, id] });
      toast.success("Note added");
    },
    onError: (err: ApiError) => {
      toast.error("Couldn't add note", { description: err.message });
    },
  });

  if (query.isLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading ticket…
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="p-8 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Card><CardContent className="p-6">
          <p className="text-signal-failed">Couldn't load ticket</p>
          <p className="text-xs text-muted-foreground mt-1">{query.error?.message ?? "Unknown error"}</p>
        </CardContent></Card>
      </div>
    );
  }

  const t = query.data;

  return (
    <div className="p-8 space-y-6 max-w-4xl">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>

      <header className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-brand-cyan">{t.external_id}</span>
          <Separator orientation="vertical" className="h-4" />
          <span className="text-xs text-muted-foreground">
            Opened {format(new Date(t.created_at), "PP")}
          </span>
        </div>
        <h1 className="font-display text-4xl">{t.subject}</h1>
      </header>

      <Card>
        <CardHeader className="flex-row items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Status</span>
            <Select
              value={t.status}
              onValueChange={(v) => updateMutation.mutate({ status: v as TicketStatus })}
            >
              <SelectTrigger className="w-[150px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="reported">Reported</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="workinprogress">In progress</SelectItem>
                <SelectItem value="pending_approval">Pending approval</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="notabug">Not a bug</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Priority</span>
            <Select
              value={t.priority}
              onValueChange={(v) => updateMutation.mutate({ priority: v as TicketPriority })}
            >
              <SelectTrigger className="w-[120px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="P1">P1 — Urgent</SelectItem>
                <SelectItem value="P2">P2 — High</SelectItem>
                <SelectItem value="P3">P3 — Medium</SelectItem>
                <SelectItem value="P4">P4 — Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap text-sm">{t.description}</p>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="font-medium">Activity</h2>
        {t.notes.length === 0 ? (
          <p className="text-xs text-muted-foreground">No activity yet.</p>
        ) : (
          <ul className="space-y-3">
            {t.notes.map((n) => (
              <li key={n.id}>
                <Card>
                  <CardContent className="p-4 flex gap-3">
                    <div className="pt-0.5">
                      {n.kind === "history" ? (
                        <History className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <MessageSquare className="h-4 w-4 text-brand-cyan" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{n.author_email ?? (n.kind === "history" ? "System" : "Unknown")}</span>
                        <span>·</span>
                        <span>{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</span>
                        {n.kind === "history" && <Badge variant="muted">History</Badge>}
                      </div>
                      <p className="mt-1 text-sm whitespace-pre-wrap">{n.body}</p>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="font-medium">Add note</h2>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a comment or update…"
          rows={4}
        />
        <Button
          onClick={() => addNoteMutation.mutate()}
          disabled={!note.trim() || addNoteMutation.isPending}
        >
          {addNoteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Post note
        </Button>
      </section>
    </div>
  );
}
